import { useContext, useEffect, useRef } from 'react';
import { WalkthroughContext } from './WalkthroughProvider';

const NOOP_WALKTHROUGH = Object.freeze({
    states: {},
    active: null,
    stepIndex: 0,
    skippedIds: [],
    completedIds: [],
    deferredIds: [],
    revisitUntil: null,
    ctx: {},
    visibleStepId: null,
    seeded: false,
    definition: null,
    choose: () => {},
    start: () => {},
    advance: () => {},
    completeStep: () => {},
    skipStep: () => {},
    stepBack: () => {},
    end: () => {},
    publishContext: () => {},
    setVisibleStep: () => {},
});

/** Walkthrough state + actions; a no-op shape when no provider is mounted (tests, storybook). */
export function useWalkthrough() {
    return useContext(WalkthroughContext) || NOOP_WALKTHROUGH;
}

function shallowEqual(a, b) {
    if (a === b) return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => Object.is(a[key], b[key]));
}

/**
 * Publish one or more context slices for the step predicates, e.g.
 * `useWalkthroughContextValue({ dataset: { hasFiles, dataCreation } })`.
 *
 * Call it where the state lives (the component owning the wizard form, the
 * data-collection panel, …). Slices are compared shallowly so fresh object
 * literals on every render do not re-publish, and each slice is cleared when
 * the publishing component unmounts.
 */
export function useWalkthroughContextValue(slices) {
    const { publishContext } = useWalkthrough();
    const lastRef = useRef({});

    useEffect(() => {
        if (!slices || typeof slices !== 'object') return;
        const changed = {};
        let any = false;
        for (const [key, value] of Object.entries(slices)) {
            if (!shallowEqual(lastRef.current[key], value)) {
                changed[key] = value;
                lastRef.current[key] = value;
                any = true;
            }
        }
        if (any) publishContext(changed);
    });

    useEffect(
        () => () => {
            const keys = Object.keys(lastRef.current);
            lastRef.current = {};
            if (keys.length) {
                publishContext(Object.fromEntries(keys.map((key) => [key, undefined])));
            }
        },
        [publishContext]
    );
}

export default useWalkthrough;
